import React, { useEffect, useState } from 'react';
import { useSolr } from '../../../hooks/useSolr';
import { Accordion, Nav } from 'react-bootstrap';
import MandalaSkeleton from '../../common/MandalaSkeleton';
import { useDictionaryStore } from '../../../hooks/useDictionaryStore';
import TermDictionaryChooser from '../TermDictionaries/TermDictionaryChooser';
import Card from 'react-bootstrap/Card';
import { IoIosArrowDown, IoIosArrowForward } from 'react-icons/io';
import TermsGlossarySearchBox from './TermsGlossarySearchbox';

const TermsGlossaryNav = ({ lang, chosenLetter, chooseLetter }) => {
    const { setStartAt, searchTerm, setSearchTerm } = useDictionaryStore();
    const [currentLetter, setCurrentLetter] = useState('');
    const [open, setOpen] = useState(false);

    // Function called when letter clicked in TOC box
    const chooseLetterClick = (e) => {
        const lid = e.target.dataset.lid.replace('terms-', '').split('|');
        //tab-tree-terms_new.alpha_leaf-terms-333929
        setStartAt(false);
        chooseLetter(lid);
        setCurrentLetter(lid[2]);
        setSearchTerm(false);
        const mobileLetters = document.getElementById('letter-row');
        if (mobileLetters) {
            mobileLetters.classList.remove('show');
        }
    };

    // Find all head letters to make TOC
    const query = 'associated_subject_ids:10523'; // TODO: check if this is for all languages or just Newar. Does Tibetan use a different letter subject tag?
    let letterQuery = {
        index: 'terms',
        params: {
            q: query,
            fq: [`tree:terms`],
            rows: 100,
            fl: '*',
            sort: `position_i ASC`,
        },
    };

    let {
        isLoading: areLettersLoading,
        data: letterdata,
        isError: isLetterError,
        error: letterError,
    } = useSolr(
        ['terms', 'glossary', lang, 'letters'],
        letterQuery,
        false,
        false
    );

    useEffect(() => {
        if (!searchTerm || searchTerm.length === 0) {
            if (chosenLetter.length === 3) {
                setCurrentLetter(chosenLetter[2]);
            }
        } else {
            setCurrentLetter([-1, '', '']);
        }
    }, [chosenLetter, searchTerm]);

    if (areLettersLoading) {
        return <MandalaSkeleton />;
    }

    if (isLetterError) {
        return <div className="error red">{letterError}</div>;
    }

    return (
        <div className="wrapper">
            <TermsGlossarySearchBox chosenLetter={chooseLetter} />{' '}
            {/* Search box and suggestions */}
            <AccordionItem aid="letter-row" title="Index">
                <Nav variant="pills" className="letter-grid">
                    {letterdata.docs.map((letter, index) => {
                        const classes = [lang];
                        const lethead = letter?.header;
                        let hash = lethead
                            .split('')
                            .map((ch, chi) => {
                                return ch.charCodeAt(0);
                            })
                            .join('');
                        // The long retroflex L letter cannot be used in css styles so changing to llr
                        const letclass = hash === '108803772' ? 'llr' : lethead;

                        if (letter.header === currentLetter) {
                            classes.push('active');
                        }
                        return (
                            <Nav.Item
                                key={`letter-nav-${index}`}
                                className={letclass}
                            >
                                <Nav.Link
                                    key={`letter-${letter.id}`}
                                    eventKey={letter.head}
                                    className={classes.join(' ')}
                                    data-lid={[
                                        letter.id,
                                        letter.name_deva[0],
                                        letter.header,
                                    ].join('|')}
                                    onClick={chooseLetterClick}
                                >
                                    {letter.name_deva[0]}
                                </Nav.Link>
                            </Nav.Item>
                        );
                    })}
                </Nav>
            </AccordionItem>
            <AccordionItem id="dictionary-row" title="Dictionaries">
                <TermDictionaryChooser lang="new" />
            </AccordionItem>
        </div>
    );
};

function AccordionItem({ title, aid, children }) {
    const LG_BREAKPOINT = 992;
    const medquery = `(min-width: ${LG_BREAKPOINT}px)`;

    // Default state = closed when under 992; open when over. TODO: Use LocalStorage to keep track?
    const defaultState = window.matchMedia(medquery).matches;
    const [open, setOpen] = useState(() => {
        const stored = localStorage.getItem(`accordion-${aid}`);
        return stored !== null ? stored === 'true' : defaultState;
    });

    const icon = open ? <IoIosArrowDown /> : <IoIosArrowForward />;

    // Add listener to collapse dictionary and letter accordions when width is mobile (i.e. not large)
    useEffect(() => {
        const mediaQuery = window.matchMedia(medquery);
        const handler = () => {
            // Only auto-open/close if there is NO saved preference
            const stored = localStorage.getItem(`accordion-${aid}`);
            if (stored === null) {
                setOpen(mediaQuery.matches);
            }
        };

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handler);
        } else {
            mediaQuery.addListener(handler);
        }

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handler);
            } else {
                mediaQuery.removeListener(handler);
            }
        };
    }, [aid]);

    useEffect(() => {
        localStorage.setItem(`accordion-${aid}`, open);
    }, [open, aid]);

    return (
        <Accordion
            activeKey={open ? '0' : null}
            onSelect={() => setOpen(!open)}
            id={aid}
        >
            <Card>
                <Accordion.Toggle as={Card.Header} eventKey="0">
                    <h3>
                        <span className="navicon">{icon}</span>
                        {title}
                    </h3>
                </Accordion.Toggle>
                <Accordion.Collapse eventKey="0">
                    <Card.Body>{children}</Card.Body>
                </Accordion.Collapse>
            </Card>
        </Accordion>
    );
}

export default TermsGlossaryNav;
